'use client';

import { useLocation, Link as RouterLink } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { useArSavedReportsList } from '@/api/rpc/hooks/accounts-receivable/useArSavedReports';
import { T, Var } from 'gt-react';

const MAX_DISPLAY = 5;

/**
 * Renders saved AR reports as sidebar sub-items under the Reports nav entry.
 * Shows up to MAX_DISPLAY reports (default report pinned first, then by lastOpenedAt).
 */
export function SavedReportNavItems() {
  const { pathname } = useLocation();
  const { reports } = useArSavedReportsList();

  if (reports.length === 0) return null;

  // Pin default report first, then sort by lastOpenedAt DESC, createdAt DESC
  const sorted = [...reports].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    const aTime = a.lastOpenedAt ?? a.createdAt;
    const bTime = b.lastOpenedAt ?? b.createdAt;
    return bTime.localeCompare(aTime);
  });

  const displayed = sorted.slice(0, MAX_DISPLAY);
  const hasMore = sorted.length > MAX_DISPLAY;

  return (
    <SidebarMenuSub>
      {displayed.map((report) => {
        const reportPath = `/accounts-receivable/reports/saved/${report.id}`;
        const isActive = pathname === reportPath;
        return (
          <SidebarMenuSubItem key={report.id}>
            <SidebarMenuSubButton asChild isActive={isActive}>
              <RouterLink to={reportPath}>
                <span className="truncate">{report.name}</span>
                {report.isDefault && (
                  <Badge
                    variant="secondary"
                    className="ml-auto text-[10px] px-1 shrink-0"
                  >
                    <T context="badge, marks the default saved AR report in the sidebar">
                      Default
                    </T>
                  </Badge>
                )}
              </RouterLink>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
      {hasMore && (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild>
            <RouterLink to="/accounts-receivable/reports">
              <span className="text-muted-foreground">
                <T context="sidebar link to view all saved AR reports">
                  View all (<Var>{reports.length}</Var>)
                </T>
              </span>
            </RouterLink>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )}
    </SidebarMenuSub>
  );
}
